// @flow

import React, { Component } from "react";
import { gql } from "@apollo/client";
import { connect, ConnectedProps } from "react-redux";
import RaisedButton from "material-ui/RaisedButton";
import FlatButton from "material-ui/FlatButton";
import CircularProgress from "material-ui/CircularProgress";
import { registerPolicyExistingAccount, PolicyState } from "../../../actions";
import type { Dispatch, State as ReduxState } from "../../../reducers";

import { SignupCardPolicy } from "../signupStepperComponents";
import { SignupCardFinalStatement } from "../signupStepperComponents";
import { USER_QUERY } from "../../ledenPortaal/policiesPage/policyPage";

type PropsParents = {
  backPressed: () => void;
  address: {
    street: string;
    city: string;
    postalCode: string;
    houseNumber: number;
    suffix: string | null;
  };
  data: {
    statutesUrl: string;
    name: string;
  };
};

type Props = PropsParents & ReduxProps;

type State = {
  isChecked: boolean;
  isMailChecked: boolean;
  isContributionChecked: boolean;
};

class OverviewStep extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      isChecked: false,
      isMailChecked: false,
      isContributionChecked: false,
    };
  }

  _performMutation() {
    const { policyData, address } = this.props;

    this.props.registerPolicy({
      quoteId: policyData.quoteId,
      birthDate: policyData.birthDate,
      premium: policyData.premium,
      lumpsum: policyData.lumpsum,
      frequency: policyData.frequency,
      packageId: policyData.packageId,
      packageName: policyData.packageName,
      insuredAmount: policyData.insuredAmount,
      firstName: policyData.firstName,
      lastNamePrefix: policyData.lastNamePrefix,
      lastName: policyData.lastName,
      initials: policyData.initials,
      gender: policyData.gender,
      postalCode: address.postalCode,
      houseNumber: address.houseNumber,
      houseNumberSuffix: address.suffix,
      city: address.city,
      street: address.street,
      iban: policyData.iban,
      accountName: policyData.accountName,
      eligibility: policyData.eligibility,
    });
  }

  render() {
    const { backPressed, isFetching, policyData, address, data } = this.props;
    const { isChecked, isMailChecked, isContributionChecked } = this.state;
    const nextDisabled = !isChecked || !isContributionChecked || !isMailChecked;

    if (isFetching) return <CircularProgress />;
    else {
      return (
        <div style={{ width: "100%", maxWidth: "650px" }}>
          <SignupCardPolicy
            policyData={{
              birthDate: policyData.birthDate,
              premium: policyData.premium,
              lumpsum: policyData.lumpsum,
              frequency: policyData.frequency,
              packageName: policyData.packageName,
              insuredAmount: policyData.insuredAmount,
              firstName: policyData.firstName,
              lastNamePrefix: policyData.lastNamePrefix,
              lastName: policyData.lastName,
              initials: policyData.initials,
              gender: policyData.gender,
              postalCode: address.postalCode,
              houseNumber: address.houseNumber,
              houseNumberSuffix: address.suffix,
              city: address.city,
              street: address.street,
              iban: policyData.iban,
              accountName: policyData.accountName,
            }}
          />
          <br />
          <SignupCardFinalStatement
            onCheckTerms={(isChecked) => {
              this.setState({ isChecked });
            }}
            onCheckMail={(isMailChecked) => {
              this.setState({ isMailChecked });
            }}
            onCheckContribution={(isContributionChecked) => {
              this.setState({ isContributionChecked });
            }}
            statutesUrl={data.statutesUrl}
            associationName={data.name}
          />
          <div
            style={{
              display: "flex",
              flexDirection: "row",
              paddingTop: "20px",
            }}
          >
            <div style={{ flex: 1, textAlign: "center" }}>
              <FlatButton
                label="Vorige"
                disableTouchRipple={true}
                disableFocusRipple={true}
                onClick={() => backPressed()}
              />
            </div>
            <div style={{ flex: 1, textAlign: "center" }}>
              <RaisedButton
                label="Aanmelden"
                disableTouchRipple={true}
                disableFocusRipple={true}
                primary={true}
                onClick={() => this._performMutation()}
                disabled={nextDisabled}
              />
            </div>
          </div>
        </div>
      );
    }
  }
}

const mapStateToProps = (state: ReduxState) => {
  const { signup } = state;

  const { quote } = signup.premium.policyInfo;

  if (!signup.userInfo.data.gender) {
    throw new Error("Gender cannot be null");
  }

  if (quote && quote.quoteExists) {
    const { data } = quote;
    const {
      quoteId,
      birthDate,
      premium,
      lumpsum,
      frequency,
      packageId,
      packageName,
      insuredAmount,
    } = data;

    return {
      policyData: {
        quoteId,
        birthDate,
        premium,
        lumpsum,
        frequency,
        packageId,
        packageName,
        insuredAmount,
        firstName: signup.userInfo.data.firstName,
        lastNamePrefix: signup.userInfo.data.lastNamePrefix,
        lastName: signup.userInfo.data.lastName,
        initials: signup.userInfo.data.initials,
        gender: signup.userInfo.data.gender,
        firstNameAccount: signup.account.data.firstName,
        lastNamePrefixAccount: signup.account.data.lastNamePrefix,
        lastNameAccount: signup.account.data.lastName,
        phone: signup.account.data.phone,
        email: signup.account.data.email,
        password: signup.account.data.password,
        iban: signup.payment.data.iban,
        accountName: signup.payment.data.accountName,
        eligibility: signup.eligibility.answers,
      },
      isFetching: signup.overview.isFetching,
    };
  } else throw new Error("Quote object is null.");
};

const mapDispatchToProps = (dispatch: Dispatch) => ({
  registerPolicy: (input: PolicyState) =>
    // @ts-ignore
    dispatch(registerPolicyExistingAccount(input, USER_QUERY)),
});

const connector = connect(mapStateToProps, mapDispatchToProps);

type ReduxProps = ConnectedProps<typeof connector>;

const Wrapper = connector(OverviewStep);

export const fragment = gql`
  fragment FinalCardFragment on Association {
    id
    statutesUrl
    name
  }
`;

const Wrapped = (props: PropsParents) => <Wrapper {...props} />;
export default Wrapped;
